import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import useSound from "use-sound";
import play from "../sounds/play.mp3";
import correct from "../sounds/correct.mp3";
import wrong from "../sounds/wrong.mp3";

export default function KbcQuiz({
  data,
  questionNumber,
  setQuestionNumber,
  setTimeOut,
  selectedAnswer,
  setSelectedAnswer,
}) {
  const [question, setQuestion] = useState(null);
  const [className, setClassName] = useState("answer");
  const [letsPlay] = useSound(play);
  const [correctAnswer] = useSound(correct);
  const [wrongAnswer] = useSound(wrong);
  const [lifelines, setLifelines] = useState({
    fiftyFifty: true,
    audiencePoll: true,
    phoneAFriend: true
  });
  const [eliminatedAnswers, setEliminatedAnswers] = useState([]);
  const [showHint, setShowHint] = useState(null);

  useEffect(() => {
    letsPlay();
  }, [letsPlay]);

  useEffect(() => {
    setQuestion(data[questionNumber - 1]);
    setEliminatedAnswers([]);
    setShowHint(null);
  }, [data, questionNumber]);

  const delay = (duration, callback) => {
    setTimeout(() => {
      callback();
    }, duration);
  };

  const useFiftyFifty = () => {
    if (!lifelines.fiftyFifty || !question) return;
    
    const correctAnswerIndex = question.answers.findIndex(a => a.correct);
    const wrongAnswers = question.answers
      .map((answer, index) => ({ answer, index }))
      .filter(({ index }) => index !== correctAnswerIndex);
    
    // Randomly select 2 wrong answers to eliminate
    const toEliminate = wrongAnswers
      .sort(() => Math.random() - 0.5)
      .slice(0, 2)
      .map(({ index }) => index);
    
    setEliminatedAnswers(toEliminate);
    setLifelines(prev => ({ ...prev, fiftyFifty: false }));
  };

  const useAudiencePoll = () => {
    if (!lifelines.audiencePoll || !question) return;
    
    const correctIndex = question.answers.findIndex(a => a.correct);
    // Simulate audience poll with bias toward correct answer
    const polls = [15, 20, 25, 40];
    const shuffled = [...polls].sort(() => Math.random() - 0.5);
    shuffled[correctIndex] = Math.max(...shuffled) + Math.floor(Math.random() * 20);
    
    setShowHint(`Audience Poll: A(${shuffled[0]}%) B(${shuffled[1]}%) C(${shuffled[2]}%) D(${shuffled[3]}%)`);
    setLifelines(prev => ({ ...prev, audiencePoll: false }));
    
    setTimeout(() => setShowHint(null), 8000);
  };

  const usePhoneAFriend = () => {
    if (!lifelines.phoneAFriend || !question) return;
    
    const hints = [
      "I'm pretty sure it's option",
      "I think the answer is",
      "My best guess would be",
      "I believe it's"
    ];
    
    const correctAnswer = question.answers.find(a => a.correct);
    const hintText = `${hints[Math.floor(Math.random() * hints.length)]} "${correctAnswer.text}"`;
    
    setShowHint(`Phone a Friend: ${hintText}`);
    setLifelines(prev => ({ ...prev, phoneAFriend: false }));
    
    setTimeout(() => setShowHint(null), 8000);
  };

  const handleClick = (a) => {
    setSelectedAnswer(a);
    setClassName("answer active");
    delay(3000, () => {
      setClassName(a.correct ? "answer correct" : "answer wrong");
    });

    delay(5000, () => {
      if (a.correct) {
        correctAnswer();
        delay(1000, () => {
          if (questionNumber === data.length) {
            setTimeOut(true); // Quiz completed
          } else {
            setQuestionNumber((prev) => prev + 1);
          }
        });
      } else {
        wrongAnswer();
        delay(1000, () => {
          setTimeOut(true);
        });
      }
    });
  };

  return (
    <motion.div 
      className="KbcQuiz"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Lifelines */}
      <motion.div 
        className="lifelines"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <button 
          className={`lifeline ${!lifelines.fiftyFifty ? 'used' : ''}`}
          onClick={useFiftyFifty}
          disabled={!lifelines.fiftyFifty || selectedAnswer}
          title="50:50 - Remove 2 wrong answers"
        >
          50:50
        </button>
        <button 
          className={`lifeline ${!lifelines.audiencePoll ? 'used' : ''}`}
          onClick={useAudiencePoll}
          disabled={!lifelines.audiencePoll || selectedAnswer}
          title="Audience Poll"
        >
          👥
        </button>
        <button 
          className={`lifeline ${!lifelines.phoneAFriend ? 'used' : ''}`}
          onClick={usePhoneAFriend}
          disabled={!lifelines.phoneAFriend || selectedAnswer}
          title="Phone a Friend"
        >
          📞
        </button>
      </motion.div>

      {/* Hint Display */}
      {showHint && (
        <motion.div 
          className="hint"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
        >
          {showHint}
        </motion.div>
      )}

      <motion.div 
        className="question"
        key={questionNumber}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, type: "spring" }}
      >
        {question?.question}
      </motion.div>
      <div className="answers">
        {question?.answers.map((a, index) => (
          <motion.div
            key={index}
            className={`${
              selectedAnswer === a ? className : "answer"
            } ${eliminatedAnswers.includes(index) ? 'eliminated' : ''}`}
            onClick={() => !selectedAnswer && !eliminatedAnswers.includes(index) && handleClick(a)}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ 
              duration: 0.5, 
              delay: index * 0.1,
              type: "spring",
              stiffness: 100
            }}
            whileHover={{ 
              scale: selectedAnswer || eliminatedAnswers.includes(index) ? 1 : 1.02,
              transition: { duration: 0.2 }
            }}
            whileTap={{ scale: 0.98 }}
            style={{
              opacity: eliminatedAnswers.includes(index) ? 0.3 : 1,
              pointerEvents: eliminatedAnswers.includes(index) ? 'none' : 'auto'
            }}
          >
            {a.text}
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}
